import React, { useState, useCallback } from 'react';
import { Button } from '@rocco/components/button';
import { DatePicker } from '@rocco/components/date-picker/date-picker';
import { Input } from '@rocco/components/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Search, LoaderCircle } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

// Hooks and utilities
import { useBuildHubData } from '~/modules/build-hub/hooks/use-build-hub-data';
import { useBuildHubUpdate } from '~/modules/build-hub/hooks/use-build-hub-update';
import { usePlaceFinder } from '~/ui-rocco/hooks/location/use-place-finder';
import { useRootData } from '~/modules/root';
import { buildFormDataForUpdate } from '~/modules/build-hub/utils/build-form-data';
import { useSetAtom } from 'jotai';
import { uiStateAtomEditBuildHubModal } from './edit-modal-state';

// Types
import type { SupportedCountry } from 'generated/graphql';
import {
    FindPlaceApiProviderEnum,
    FindPlaceTypeEnum,
    BuildHubTypeEnum,
} from 'generated/graphql';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

// Utilities
import { formatLocation } from '@rocco/utils/format-location';
import type { LocationPlace } from '@rocco/ui/location/types/location-place';

// Validation
import { ProjectTitleSchema } from '~/modules/build-hub-creator/validation/schemas';
import { toast } from 'sonner';

// Constants
import {
    BUILD_HUB_TITLE_MAX_LENGTH,
    BUILD_HUB_TITLE_MAX_LENGTH_WARNING,
    BUILD_HUB_TITLE_MAX_LENGTH_WARNING_THRESHOLD,
} from '~/modules/build-hub-creator/configs/constants';
import { isBudgetRange } from '@modules/build-hub/utils/format-budget';
import { ProjectBudgetStep } from '@modules/build-hub-creator/components/steps/ProjectBudgetStep';
import type { BudgetRange } from '@modules/build-hub-creator/api/types';
import {
    parseDateFromYYYYMMDD,
    formatDateForApi,
} from '~/modules/build-hub/utils/date-utils';

interface EditBuildHubFormProps {
    buildHubId: string;
}

interface FormData {
    title: string;
    description: string;
    projectType: BuildHubTypeEnum;
    activeStageURLSegment: string | null; // URLSegment of the active stage
    location: {
        selectedLocation: DetailedPlace | null;
        keyword: string;
    };
    timeline: {
        startDate: Date | null;
        endDate: Date | null;
        isNotSure: boolean;
    };
    budget: BudgetRange;
}

interface FormErrors {
    title?: string;
    description?: string;
    status?: string;
    location?: string;
    timeline?: string;
}

export const EditBuildHubForm = ({ buildHubId }: EditBuildHubFormProps) => {
    const { buildHub, stageConfig } = useBuildHubData();
    const { localisation } = useRootData();
    const setEditModalState = useSetAtom(uiStateAtomEditBuildHubModal);

    // Stable success callback to prevent re-renders
    const handleSuccess = useCallback(() => {
        // Close modal on success
        setEditModalState({ isOpen: false, buildHubId: '' });
        toast.success(`${buildHub.title} updated successfully`, {
            action: {
                label: 'Dismiss',
                onClick: () => toast.dismiss(),
            },
        });
    }, [setEditModalState, buildHub]);

    // Update hook with success callback
    const { updateBuildHub, isUpdating, error } = useBuildHubUpdate({
        onSuccess: handleSuccess,
    });

    // Generate status options from the buildHub's available stages
    const statusOptions =
        buildHub?.availableStages
            ?.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((stage) => ({
                value: stage.urlSegment,
                label: stage.title,
            })) || [];

    // Find the active stage from availableStages
    const activeStage = buildHub?.availableStages?.find(
        (stage) => stage.isActive
    );

    // Initialize form data from existing build hub
    const [formData, setFormData] = useState<FormData>({
        title: buildHub?.title || '',
        description: buildHub?.description || '',
        projectType: buildHub?.type
            ? (buildHub.type as BuildHubTypeEnum)
            : BuildHubTypeEnum.Residential,
        activeStageURLSegment: activeStage?.urlSegment || null,
        location: {
            selectedLocation: buildHub?.locationData ?? null,
            keyword: buildHub?.location || '',
        },
        timeline: {
            startDate: parseDateFromYYYYMMDD(buildHub?.startDate),
            endDate: parseDateFromYYYYMMDD(buildHub?.endDate),
            isNotSure: false,
        },
        budget: {
            min: buildHub?.budget?.min,
            max: buildHub?.budget?.max,
        },
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [showLocationSuggestions, setShowLocationSuggestions] =
        useState(false);

    // Location search
    const {
        places,
        handlePlaceFind,
        resetPlaces,
        loading: placeFinderLoading,
    } = usePlaceFinder({
        country: localisation.country as SupportedCountry,
        type: FindPlaceTypeEnum.All,
        apiProvider: FindPlaceApiProviderEnum.Mapbox,
    });

    // Helper function to update form data
    const updateFormData = (updates: Partial<FormData>) => {
        setFormData((prev) => ({ ...prev, ...updates }));
    };

    // Validation
    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Title validation
        const titleResult = ProjectTitleSchema.safeParse(formData.title);
        if (!titleResult.success) {
            newErrors.title =
                titleResult.error.errors[0]?.message || 'Invalid title';
        }

        // Timeline validation
        if (
            formData.timeline.startDate &&
            formData.timeline.endDate &&
            !formData.timeline.isNotSure
        ) {
            if (formData.timeline.endDate < formData.timeline.startDate) {
                newErrors.timeline =
                    'End date cannot be earlier than start date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Event handlers
    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFormData({ title: e.target.value });
    };

    const handleStatusChange = (value: string) => {
        updateFormData({ activeStageURLSegment: value });
    };

    const handleLocationKeywordChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        updateFormData({
            location: { ...formData.location, keyword: value },
        });

        if (value.trim().length > 2) {
            handlePlaceFind(value, 10);
            setShowLocationSuggestions(true);
        } else {
            resetPlaces();
            setShowLocationSuggestions(false);
        }

        if (value.trim().length === 0) {
            updateFormData({
                location: { selectedLocation: null, keyword: '' },
            });
            setShowLocationSuggestions(false);
        }
    };

    const handleLocationSelect = (place: DetailedPlace) => {
        const formattedLocation =
            place.fullAddress ||
            [place.suburb, place.district || place.city, place.region]
                .filter(Boolean)
                .join(', ');

        updateFormData({
            location: {
                selectedLocation: place,
                keyword: formattedLocation,
            },
        });
        setShowLocationSuggestions(false);
        resetPlaces();
    };

    const handleLocationClear = () => {
        updateFormData({
            location: { selectedLocation: null, keyword: '' },
        });
        setShowLocationSuggestions(false);
        resetPlaces();
    };

    const handleStartDateChange = (date: Date | undefined) => {
        updateFormData({
            timeline: { ...formData.timeline, startDate: date || null },
        });

        // Clear timeline error when start date changes
        if (errors.timeline) {
            setErrors((prev) => ({ ...prev, timeline: undefined }));
        }
    };

    const handleEndDateChange = (date: Date | undefined) => {
        updateFormData({
            timeline: { ...formData.timeline, endDate: date || null },
        });

        // Clear timeline error when end date changes
        if (errors.timeline) {
            setErrors((prev) => ({ ...prev, timeline: undefined }));
        }
    };

    const handleNotSureClick = () => {
        const newIsNotSure = !formData.timeline.isNotSure;
        updateFormData({
            timeline: {
                ...formData.timeline,
                isNotSure: newIsNotSure,
                endDate: newIsNotSure ? null : formData.timeline.endDate,
            },
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            // Convert Date objects to strings for the API
            const timelineForApi = {
                startDate: formatDateForApi(formData.timeline.startDate),
                endDate: formatDateForApi(formData.timeline.endDate),
                isNotSure: formData.timeline.isNotSure,
            };

            // Build FormData using the utility
            const formDataObj = buildFormDataForUpdate(buildHubId, {
                title: formData.title,
                description: formData.description,
                projectType: formData.projectType,
                activeStageURLSegment:
                    formData.activeStageURLSegment || undefined,
                location: formData.location,
                timeline: timelineForApi,
                budget: formData.budget,
            });

            // Submit update
            updateBuildHub(formDataObj);
        } catch (error) {
            console.error('Failed to update build hub:', error);
            // TODO: Handle error state
        }
    };

    const handleCancel = () => {
        setEditModalState({ isOpen: false, buildHubId: '' });
    };

    // Format place for display in suggestions
    const formatPlaceForDisplay = (place: DetailedPlace): string => {
        if (place.fullAddress) {
            return place.fullAddress;
        }

        const locationPlace: LocationPlace = {
            suburb: place.suburb,
            district: place.district,
            city: place.city,
            region: place.region,
            state: place.state,
            fullAddress: place.fullAddress,
            latitude: place.latitude,
            longitude: place.longitude,
            country: place.country,
            address1: place.address1,
            address2: place.address2,
            addressID: place.addressID,
            depth: place.depth,
            parentID: place.parentID,
            placeName: place.placeName,
            postCode: place.postCode,
            serviceableAreaID: place.serviceableAreaID,
        };

        return formatLocation(locationPlace);
    };

    const maxCharacters = BUILD_HUB_TITLE_MAX_LENGTH;
    const remainingCharacters = maxCharacters - formData.title.length;

    return (
        <div className="max-w-2xl">
            <div className="mb-8">
                <h2 className="text-2xl font-medium text-gray-900">
                    Edit Project
                </h2>
                <p className="mt-2 text-gray-600">
                    Update your project details below.
                </p>
            </div>

            {/* Error Display */}
            {error && (
                <div className="mb-6 rounded-lg bg-red-50 p-4">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} id={'build-hub-edit-form'}>
                <div className="mb-10 divide-y-2 divide-gray-200 [&>*:not(:first-child)]:pt-5 [&>*:not(:last-child)]:pb-5">
                    {/* Project Title */}
                    <div>
                        <FormSectionLabel
                            title="Project Title"
                            description="Rename your project to reflect your vision."
                            htmlFor="project-title"
                        />
                        <Input
                            id="project-title"
                            type="text"
                            color="gray"
                            text="default"
                            autoComplete="off"
                            value={formData.title}
                            onChange={handleTitleChange}
                            placeholder="My first Project"
                            maxLength={maxCharacters}
                        />
                        {errors.title && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.title}
                            </p>
                        )}
                        <div className="mt-2 flex items-center justify-end">
                            {remainingCharacters <
                                BUILD_HUB_TITLE_MAX_LENGTH_WARNING && (
                                <span
                                    className={`text-xs ${
                                        remainingCharacters <
                                        BUILD_HUB_TITLE_MAX_LENGTH_WARNING_THRESHOLD
                                            ? 'text-brick-500'
                                            : 'text-gray-500'
                                    }`}
                                >
                                    {remainingCharacters} characters remaining
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Project Status */}
                    <div>
                        <FormSectionLabel
                            title="Project Stage"
                            description="Update the current stage of your project to help track progress."
                        />
                        <Select
                            value={formData.activeStageURLSegment || undefined}
                            onValueChange={handleStatusChange}
                        >
                            <SelectTrigger className="w-full" color="gray">
                                <SelectValue placeholder="Select project stage" />
                            </SelectTrigger>
                            <SelectContent>
                                {statusOptions.map((option) => (
                                    <SelectItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Project Location */}
                    <div>
                        <FormSectionLabel
                            title="Project Location"
                            description="Tell us the location of your project so we can match you with the right resources for the area."
                            htmlFor="project-location"
                        />
                        <div className="relative">
                            <Input
                                id="project-location"
                                type="text"
                                color="gray"
                                text="default"
                                value={formData.location.keyword}
                                autoComplete="off"
                                onChange={handleLocationKeywordChange}
                                placeholder="Enter a location (suburb, postcode, or address)"
                                showClearButton={false}
                                postfix={
                                    placeFinderLoading ? (
                                        <LoaderCircle className="size-4 animate-spin" />
                                    ) : (
                                        <Search
                                            className={cn(
                                                'size-4',
                                                formData.location.keyword
                                                    .length === 0 &&
                                                    'opacity-50'
                                            )}
                                        />
                                    )
                                }
                                onClear={handleLocationClear}
                            />

                            {/* Location Suggestions */}
                            {showLocationSuggestions && places.length > 0 && (
                                <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                                    {places.map((place, index) => (
                                        <div
                                            key={index}
                                            className="cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                                            onClick={() =>
                                                handleLocationSelect(place)
                                            }
                                        >
                                            <div className="text-sm text-gray-900">
                                                {formatPlaceForDisplay(place)}
                                            </div>
                                            {place.postCode && (
                                                <div className="mt-1 text-xs text-gray-500">
                                                    {place.postCode}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Project Timeline */}
                    <div>
                        <FormSectionLabel
                            title="Project Timeline"
                            description="Do you have a start date or timeframe in mind?"
                            htmlFor="project-timeline"
                        />

                        <div className="space-y-4">
                            <div>
                                <label
                                    htmlFor="start-date"
                                    className="mb-2 block text-sm font-medium text-gray-700"
                                >
                                    Start Date
                                </label>
                                <div className="flex">
                                    <DatePicker
                                        value={
                                            formData.timeline.startDate ||
                                            undefined
                                        }
                                        onChange={handleStartDateChange}
                                        placeholder="Select start date"
                                        showTodayButton={true}
                                        className="flex-1"
                                        buttonProps={{
                                            color: 'transparent',
                                            className:
                                                'bg-gray-100 rounded-lg px-4 py-3 flex-1 justify-start font-normal',
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="end-date"
                                    className="mb-2 block text-sm font-medium text-gray-700"
                                >
                                    End Date
                                </label>
                                <div className="flex">
                                    <DatePicker
                                        value={
                                            formData.timeline.endDate ||
                                            undefined
                                        }
                                        onChange={handleEndDateChange}
                                        placeholder="Select end date"
                                        showTodayButton={true}
                                        minDate={
                                            formData.timeline.startDate ||
                                            undefined
                                        }
                                        className="flex-1"
                                        buttonProps={{
                                            color: 'transparent',
                                            className:
                                                'bg-gray-100 rounded-lg px-4 py-3 flex-1 justify-start font-normal',
                                        }}
                                        customControl={({ close }) => (
                                            <Button
                                                type="button"
                                                color="transparent"
                                                onClick={() => {
                                                    handleNotSureClick();
                                                    close();
                                                }}
                                                size="narrow"
                                                className="justify-center text-sm"
                                            >
                                                {`I'm not sure`}
                                            </Button>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        {errors.timeline && (
                            <p className="mt-2 text-sm text-red-600">
                                {errors.timeline}
                            </p>
                        )}
                    </div>

                    {/* Project Budget */}
                    <ProjectBudgetStep
                        initialData={{
                            ...formData.budget,
                            projectScope: buildHub?.scope ?? undefined,
                            stageConfig: stageConfig ?? undefined,
                        }}
                        isEditForm={true}
                        onDataChange={(data) => {
                            if (isBudgetRange(data)) {
                                updateFormData({ budget: data });
                            }
                        }}
                        setIsValid={() => {}}
                        formHeader={
                            <FormSectionLabel
                                title="Project Budget"
                                description="Knowing your budget helps us tailor suggestions and connect you with appropriate resources. What's your range?"
                                htmlFor="project-budget"
                            />
                        }
                        projectScope={buildHub?.scope ?? undefined}
                        stageConfig={stageConfig ?? undefined}
                    />
                </div>

                {/* Form Actions */}
                <div className="sticky bottom-0 z-1 flex justify-end space-x-3 border-t border-gray-200 bg-white pb-6 pt-6">
                    <Button
                        type="button"
                        color="outline"
                        size="default"
                        disabled={isUpdating}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        size="default"
                        disabled={isUpdating}
                    >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

const FormSectionLabel = ({
    title,
    description,
    htmlFor,
}: {
    title: string;
    description: string;
    htmlFor?: string;
}) => {
    return (
        <>
            <label
                {...(htmlFor && { htmlFor })}
                className="mb-1 block text-base font-medium text-gray-900"
            >
                {title}
            </label>
            <p className="mb-4 text-sm text-gray-600">{description}</p>
        </>
    );
};
